import React, { Children, cloneElement, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Lives in <body> so that no scrolling container clips it; hidden on any scroll.
function useTooltip(label) {
  const [box, setBox] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    if (!box) return undefined;
    const hide = () => setBox(null);
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [box]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (e) => {
    if (!label) return;
    const r = e.currentTarget.getBoundingClientRect();
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setBox(r), 250);
  };
  const hide = () => {
    clearTimeout(timer.current);
    setBox(null);
  };
  let tip = null;
  if (box) {
    const below = box.top < 44;
    const x =
      box.right > window.innerWidth - 120
        ? { left: box.right, tx: "-100%" }
        : box.left < 120
          ? { left: box.left, tx: "0" }
          : { left: box.left + box.width / 2, tx: "-50%" };
    tip = createPortal(
      <span
        role="tooltip"
        // The admin's usual tooltip (components/tooltips/Tooltips, « Marquer comme
        // « Complété » »): dark grey, 14px, 8px by 16px, at most 280px wide.
        className="tooltip-in pointer-events-none fixed z-[70] w-max max-w-[280px] rounded bg-gray-800 px-4 py-2 text-sm font-normal leading-5 tracking-[0.02em] text-white"
        style={{
          left: x.left,
          top: below ? box.bottom + 6 : box.top - 6,
          transform: `translate(${x.tx}, ${below ? "0" : "-100%"})`,
        }}
      >
        {label}
      </span>,
      document.body
    );
  }
  return {
    handlers: {
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
      onMouseDown: hide,
    },
    tip,
  };
}

// Puts the tooltip on its only child, without wrapping it.
const Tooltip = ({ label, children }) => {
  const { handlers, tip } = useTooltip(label);
  const child = Children.only(children);
  const merged = Object.fromEntries(
    Object.entries(handlers).map(([k, fn]) => [
      k,
      (e) => {
        fn(e);
        child.props[k]?.(e);
      },
    ])
  );
  return (
    <>
      {cloneElement(child, merged)}
      {tip}
    </>
  );
};

export default Tooltip;
